import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "headline", "subtitle", "cta", "badge", "scrollIndicator", "nav"]

  connect() {
    if (!window.gsap || !window.ScrollTrigger) return

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.tweens = []
    this.triggers = []
    this.timeline = null

    this.animateEntrance()
    this.setupParallax()
    this.setupNavScroll()
  }

  disconnect() {
    if (this.timeline) {
      this.timeline.kill()
    }
    this.tweens.forEach((tween) => {
      if (tween.scrollTrigger) tween.scrollTrigger.kill()
      tween.kill()
    })
    this.triggers.forEach((trigger) => trigger.kill())
    this.tweens = []
    this.triggers = []
  }

  animateEntrance() {
    if (this.reducedMotion) {
      const targets = [
        this.hasHeadlineTarget ? Array.from(this.headlineTarget.children) : [],
        this.hasSubtitleTarget ? [this.subtitleTarget] : [],
        this.ctaTargets,
        this.hasBadgeTarget ? [this.badgeTarget] : [],
        this.hasScrollIndicatorTarget ? [this.scrollIndicatorTarget] : [],
      ].flat()
      window.gsap.set(targets, { opacity: 1, y: 0 })
      return
    }

    this.timeline = window.gsap.timeline({ delay: 0.3 })

    if (this.hasHeadlineTarget) {
      this.timeline.from(this.headlineTarget.children, {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out",
      })
    }

    if (this.hasSubtitleTarget) {
      this.timeline.from(
        this.subtitleTarget,
        { y: 30, opacity: 0, duration: 0.6, ease: "power3.out" },
        "-=0.4"
      )
    }

    if (this.ctaTargets.length > 0) {
      this.timeline.from(
        this.ctaTargets,
        { y: 20, opacity: 0, duration: 0.5, stagger: 0.1, ease: "power3.out" },
        "-=0.3"
      )
    }

    if (this.hasBadgeTarget) {
      this.timeline.from(
        this.badgeTarget,
        { y: 20, opacity: 0, duration: 0.5, ease: "power3.out" },
        "-=0.2"
      )
    }

    if (this.hasScrollIndicatorTarget) {
      const bounce = window.gsap.to(this.scrollIndicatorTarget, {
        y: 8,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut",
      })
      this.tweens.push(bounce)
    }
  }

  setupParallax() {
    if (this.reducedMotion) return

    if (this.hasVideoTarget) {
      const videoTween = window.gsap.to(this.videoTarget.parentElement, {
        yPercent: 30,
        scale: 1.1,
        scrollTrigger: {
          trigger: this.element,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      })
      this.tweens.push(videoTween)
    }

    const heroContent = this.element.querySelector(".relative.z-10")
    if (heroContent) {
      const contentTween = window.gsap.to(heroContent, {
        opacity: 0,
        y: -50,
        scrollTrigger: {
          trigger: this.element,
          start: "top top",
          end: "40% top",
          scrub: true,
        },
      })
      this.tweens.push(contentTween)
    }
  }

  setupNavScroll() {
    if (!this.hasNavTarget) return

    const navTarget = this.navTarget
    const trigger = window.ScrollTrigger.create({
      start: 80,
      onUpdate: (self) => {
        if (self.scroll() > 80) {
          navTarget.classList.add("scrolled")
        } else {
          navTarget.classList.remove("scrolled")
        }
      },
    })
    this.triggers.push(trigger)
  }
}
